import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, StatusBadge, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

export default function Offers() {
  const { user } = useAuth();
  const [offers, setOffers] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/mkt/offers").then((r) => setOffers(r.data.offers)).catch((e) => { toast.error(errMsg(e)); setOffers([]); });
  useEffect(() => { load(); }, []);

  const accept = async (of) => {
    setBusy(true);
    try {
      await api.post(`/mkt/offers/${of.id}/accept`);
      toast.success("Offer accepted — complete payment in My Orders");
      navigate("/mkt/orders");
    } catch (e) { toast.error(errMsg(e)); load(); } finally { setBusy(false); }
  };

  const received = (offers || []).filter((o) => o.buyer_id === (user?.id || user?._id));
  const sent = (offers || []).filter((o) => o.freelancer_id === (user?.id || user?._id));

  const OfferCard = ({ of, mine }) => (
    <div data-testid={`offer-${of.id}`} className="bg-card border border-border rounded-xl p-5 flex flex-wrap items-center gap-4">
      <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB)" }}>
        <IndianRupee className="h-5 w-5 text-white" />
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-display font-bold text-sm">{mine ? `To: ${of.buyer_id === of.freelancer_id ? "" : ""}` : `From: ${of.freelancer_name}`}</p>
          <StatusBadge status={of.status} />
        </div>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{of.description}</p>
        <p className="text-[11px] text-muted-foreground mt-1">{of.delivery_days}d delivery · {of.revisions} revision{of.revisions === 1 ? "" : "s"} · {fmtDateTime(of.created_at)}</p>
      </div>
      <p className="font-display font-extrabold text-xl">{fmtINR(of.price)}</p>
      {!mine && of.status === "open" && (
        <Button size="sm" data-testid={`offer-accept-${of.id}`} onClick={() => accept(of)} disabled={busy} className="rounded-full font-bold gradient-bg text-white border-0">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept & Pay"}
        </Button>)}
    </div>
  );

  return (
    <div className="space-y-2" data-testid="offers-page">
      <PageHeader title="Custom Offers" subtitle="Personalised quotes from freelancers" testid="offers-title" />
      {!offers ? <LoadingRows rows={3} /> : offers.length === 0 ? (
        <EmptyState title="No offers yet" body="Freelancers can send you custom offers from the Messages page.">
          <Button data-testid="offers-goto-messages" onClick={() => navigate("/mkt/messages")} className="rounded-full font-bold gradient-bg text-white border-0">Open Messages</Button>
        </EmptyState>
      ) : (
        <Tabs defaultValue="received">
          <TabsList data-testid="offers-tabs">
            <TabsTrigger value="received" data-testid="offers-tab-received">Received ({received.length})</TabsTrigger>
            <TabsTrigger value="sent" data-testid="offers-tab-sent">Sent ({sent.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="received" className="mt-4 space-y-3">
            {received.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No offers received.</p> : received.map((o) => <OfferCard key={o.id} of={o} />)}
          </TabsContent>
          <TabsContent value="sent" className="mt-4 space-y-3">
            {sent.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No offers sent.</p> : sent.map((o) => <OfferCard key={o.id} of={o} mine />)}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
